import type { ParkingFootprint } from "./parkingTypes";

export type ParkingAreaShape = {
  id: string;
  name: string;
  polygon: [number, number][];
};

const parseApiCoordinate = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim().replace(",", ".");
    if (!trimmed.length) {
      return null;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const normalizeApiPoint = (input: unknown): [number, number] | null => {
  if (Array.isArray(input)) {
    if (input.length < 2) {
      return null;
    }
    const lng = parseApiCoordinate(input[0]);
    const lat = parseApiCoordinate(input[1]);
    if (lng === null || lat === null) {
      return null;
    }
    return [Number(lng.toFixed(6)), Number(lat.toFixed(6))];
  }
  if (input && typeof input === "object") {
    const record = input as Record<string, unknown>;
    const lat =
      parseApiCoordinate(record.lat) ??
      parseApiCoordinate(record.latitude) ??
      parseApiCoordinate(record.Lat) ??
      parseApiCoordinate(record.LAT) ??
      parseApiCoordinate(record.y) ??
      parseApiCoordinate(record.Y);
    const lng =
      parseApiCoordinate(record.lng) ??
      parseApiCoordinate(record.lon) ??
      parseApiCoordinate(record.longitude) ??
      parseApiCoordinate(record.long) ??
      parseApiCoordinate(record.Lng) ??
      parseApiCoordinate(record.Lon) ??
      parseApiCoordinate(record.Long) ??
      parseApiCoordinate(record.x) ??
      parseApiCoordinate(record.X);
    if (lng === null || lat === null) {
      return null;
    }
    return [Number(lng.toFixed(6)), Number(lat.toFixed(6))];
  }
  if (typeof input === "string") {
    const trimmed = input.trim();
    if (!trimmed.length) {
      return null;
    }
    if (trimmed.includes(";")) {
      const tokens = trimmed.split(";").map((part) => part.trim());
      if (tokens.length >= 2) {
        const lng = parseApiCoordinate(tokens[0]);
        const lat = parseApiCoordinate(tokens[1]);
        if (lng !== null && lat !== null) {
          return [Number(lng.toFixed(6)), Number(lat.toFixed(6))];
        }
      }
    }
    const parts = trimmed.split(/[, ]+/).filter(Boolean);
    if (parts.length >= 2) {
      const lng = parseApiCoordinate(parts[0]);
      const lat = parseApiCoordinate(parts[1]);
      if (lng !== null && lat !== null) {
        return [Number(lng.toFixed(6)), Number(lat.toFixed(6))];
      }
    }
  }
  return null;
};

const flattenPolygonPoints = (raw: unknown): unknown[] => {
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed.length) {
      return [];
    }
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed = JSON.parse(trimmed) as unknown;
        return flattenPolygonPoints(parsed);
      } catch {
        const sanitized = trimmed.replace(/^\[+/, "").replace(/\]+$/, "");
        if (!sanitized.length) {
          return [];
        }
        const approxPairs = sanitized.split("],[").map((segment) => segment.replace(/[\[\]]+/g, ""));
        const approxPoints = approxPairs
          .map((segment) => segment.split(/[,;\s]+/).filter(Boolean))
          .filter((tokens) => tokens.length >= 2)
          .map((tokens) => [tokens[0], tokens[1]]);
        if (approxPoints.length) {
          return approxPoints;
        }
      }
    }
    const looseTokens = trimmed.split(/\s*\)\s*|\s*\(\s*/).filter(Boolean);
    if (looseTokens.length) {
      const fallbackPoints = looseTokens
        .map((entry) => entry.split(/[,;\s]+/).filter(Boolean))
        .filter((tokens) => tokens.length >= 2)
        .map((tokens) => [tokens[0], tokens[1]]);
      if (fallbackPoints.length) {
        return fallbackPoints;
      }
    }
    return [];
  }
  if (Array.isArray(raw)) {
    if (raw.length > 0) {
      const first = raw[0];
      if (Array.isArray(first) && first.length > 0 && Array.isArray((first as unknown[])[0])) {
        return flattenPolygonPoints(first as unknown[]);
      }
    }
    return raw;
  }
  if (raw && typeof raw === "object") {
    const record = raw as Record<string, unknown>;
    const candidates = ["coordinates", "polygon", "coords", "points", "vertices", "path"];
    for (const key of candidates) {
      if (Array.isArray(record[key])) {
        return flattenPolygonPoints(record[key]);
      }
    }
  }
  return [];
};

const normalizePolygonFromApi = (raw: unknown): [number, number][] => {
  const points = flattenPolygonPoints(raw);
  if (!Array.isArray(points) || !points.length) {
    return [];
  }
  const normalized: [number, number][] = [];
  points.forEach((candidate) => {
    const point = normalizeApiPoint(candidate);
    if (!point) {
      return;
    }
    const last = normalized[normalized.length - 1];
    if (!last || last[0] !== point[0] || last[1] !== point[1]) {
      normalized.push(point);
    }
  });
  if (normalized.length >= 3) {
    const first = normalized[0];
    const last = normalized[normalized.length - 1];
    if (first[0] === last[0] && first[1] === last[1]) {
      normalized.pop();
    }
  }
  return normalized;
};

const extractParkingAreaArray = (payload: unknown, depth = 0): unknown[] | null => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (!payload || typeof payload !== "object" || depth > 4) {
    return null;
  }
  const record = payload as Record<string, unknown>;
  const candidates = [record["_data_"], record._data_, record.data, record.area, record.areas, record.list, record.results];
  for (const candidate of candidates) {
    if (!candidate || candidate === payload) {
      continue;
    }
    const extracted = extractParkingAreaArray(candidate, depth + 1);
    if (extracted) {
      return extracted;
    }
  }
  return null;
};

const normalizeAreaId = (value: unknown, fallback: string): string => {
  if (typeof value === "string" && value.trim().length > 0) {
    return value.trim();
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return fallback;
};

export const parseParkingAreasResponse = (payload: unknown): ParkingAreaShape[] => {
  const source = extractParkingAreaArray(payload);
  if (!source) {
    return [];
  }

  return source
    .map((entry, index) => {
      if (!entry || typeof entry !== "object") {
        return null;
      }
      const record = entry as Record<string, unknown>;
      const polygonSource =
        record.polygon ?? record.geometry ?? record.coords ?? record.coordinates ?? record.points ?? record.vertices ?? record.path;
      const polygon = normalizePolygonFromApi(polygonSource);
      if (polygon.length < 3) {
        return null;
      }
      const name =
        (typeof record.name === "string" && record.name.trim().length > 0 ? record.name.trim() : null) ??
        `Strefa ${index + 1}`;
      const areaId = normalizeAreaId(record.area_id ?? record.id ?? record.areaId, `area-${index + 1}`);
      return {
        id: areaId,
        name,
        polygon,
      };
    })
    .filter((entry): entry is ParkingAreaShape => Boolean(entry));
};

export const pickFootprintFromAreas = (areas: ParkingAreaShape[]): ParkingFootprint | null => {
  if (!areas.length) {
    return null;
  }
  const sorted = [...areas].sort((a, b) => b.polygon.length - a.polygon.length);
  const primary = sorted[0];
  if (!primary || primary.polygon.length < 3) {
    return null;
  }
  return { polygon: primary.polygon };
};
